import { IconButton, MenuItem, Tooltip } from "@mui/material";
import styled, { css } from "styled-components/macro";
import {
  useIsAdmin,
  useEffectOnce,
  useFocusRange,
  useVisibleProcessIds,
} from "utils/customHooks/genericHooks";
import { Download, Image } from "@mui/icons-material";
import html2canvas from "html2canvas";
import { format } from "date-fns";
import { useSelector } from "store/useSelector";
import { darkBlue } from "constants/colors";
import { atom, useAtom } from "jotai";
import { useMount } from "react-use";
import { allTimelinesLoadedAtom } from "components/Dashboard/Timeline/ConnectedTimeline";
import { startTransition, useCallback, useEffect, useState } from "react";
import Spinner from "components/SimpleComponents/Spinner/Spinner";
import { useTimelinesStore } from "components/Dashboard/Timeline/TimelineCardControls/timelinesStore";
import { useTimelineTooltipStore } from "components/Dashboard/Timeline/TimelineTooltip/timelineTooltipStore";

export function DownloadTimelineImageButton() {
  return (
    <SnapshotCanvasTimelineElementButton
      elementName={AUTO_DOWNLOAD_IMG.timeline}
      selector={"[class*='TimelinesStyles__TimeAxisStyles']"}
      onBeforeSnapshot={(elementToSnapshot: HTMLElement) => {
        // ! too slow on many-timelines pages e.g. sbdwarrington
        // elementToSnapshot.style.padding = "10px";
      }}
      onAfterSnapshot={(elementToSnapshot: HTMLElement) => {
        // elementToSnapshot.style.padding = "";
      }}
    />
  );
}

export function DownloadPnlChartImageButton() {
  const shouldAutoDownload = window.location.search.includes(
    `autoDownload=${AUTO_DOWNLOAD_IMG.pnl}`
  );
  return shouldAutoDownload ? (
    <SnapshotElementButton
      elementName={AUTO_DOWNLOAD_IMG.pnl}
      selector={`[data-component-name="PnL Chart"]`}
      customCss={css`
        top: 2px;
        right: 2px;
      `}
      onBeforeSnapshot={(elementToSnapshot: HTMLElement) => {
        elementToSnapshot.style.padding = "10px";
      }}
      onAfterSnapshot={(elementToSnapshot: HTMLElement) => {
        elementToSnapshot.style.padding = "";
      }}
    />
  ) : null;
}

export function DownloadPnlChartImageMenuItem({
  handleClose,
}: {
  handleClose: () => void;
}) {
  return (
    <SnapshotElementButton
      elementName={AUTO_DOWNLOAD_IMG.pnl}
      selector={`[data-component-name="PnL Chart"]`}
      customCss={css`
        top: 2px;
        right: 2px;
      `}
      onBeforeSnapshot={(elementToSnapshot: HTMLElement) => {
        elementToSnapshot.style.padding = "10px";
      }}
      onAfterSnapshot={(elementToSnapshot: HTMLElement) => {
        elementToSnapshot.style.padding = "";
      }}
      isMenuItem={true}
      onMenuItemClick={handleClose}
    />
  );
}

/** Download button for an element; also handles autoDownload via url */
function SnapshotElementButton({
  elementName,
  selector,
  customCss,
  onBeforeSnapshot,
  onAfterSnapshot,
  isMenuItem,
  onMenuItemClick,
}: {
  elementName: string;
  /** CSS selector for element you want to snapshot */
  selector: string;
  customCss?: string;
  onBeforeSnapshot?: (elementToSnapshot: HTMLElement) => void;
  onAfterSnapshot?: (elementToSnapshot: HTMLElement) => void;
  isMenuItem?: boolean;
  onMenuItemClick?: () => void;
}) {
  const focusRange = useFocusRange();
  const isAdminUser = useIsAdmin();
  const [isTakingSnapshot, setIsTakingSnapshot] = useIsTakingSnapshot();

  const shouldAutoDownload = window.location.search.includes(
    `autoDownload=${elementName}`
  );
  const visibleProcessIds = useVisibleProcessIds();

  const handleDownloadElementPng = useCallback(() => {
    // grab the element [class*="TimelinesStyles__TimeAxisStyles"] and download as png to the user
    const elementToSnapshot = document.querySelector<HTMLElement>(selector);
    if (elementToSnapshot) {
      onBeforeSnapshot?.(elementToSnapshot);
      startTransition(() => {
        setIsTakingSnapshot(true);
        html2canvas(elementToSnapshot)
          .then(canvas => {
            const imgData = canvas.toDataURL("image/png");
            const downloadLink = document.createElement("a");
            downloadLink.href = imgData;
            const focusRangeReadable = `${format(
              focusRange?.ge ?? 0,
              "yyyy-MM-dd"
            )}_${format(focusRange?.lt ?? 0, "yyyy-MM-dd")}`;
            downloadLink.download = `${elementName}_${visibleProcessIds.join(
              ","
            )}_${focusRangeReadable}.png`;
            downloadLink.click();
          })
          .then(() => {
            console.log(`downloaded ${elementName} png`);
            onAfterSnapshot?.(elementToSnapshot);
            setIsTakingSnapshot(false);
          });
      });
    }
  }, [
    elementName,
    focusRange,
    onAfterSnapshot,
    onBeforeSnapshot,
    selector,
    setIsTakingSnapshot,
    visibleProcessIds,
  ]);

  const segments = useSelector(state => state.legacy.segments);
  // check that we have segments for all visible processes
  const segmentsForProcess = visibleProcessIds.map(id => segments[id]);

  // ensure segments span the entire visibleRange (no gaps in the timeline)
  const [allTimelinesLoaded] = useAtom(allTimelinesLoadedAtom);
  const timelinesLoadedValues = Object.values(allTimelinesLoaded);

  const haveAllSegments =
    segmentsForProcess.length > 0 &&
    segmentsForProcess.every(segments => segments && segments.length > 0) &&
    timelinesLoadedValues.length === visibleProcessIds.length &&
    timelinesLoadedValues.every(isLoaded => isLoaded);

  // sometimes (e.g. sbdwarrington) there are gaps in the segments so haveAllSegments will never become true
  // -> instead, set a maximum timeout to wait before taking the snapshot regardless
  const [timedOut, setTimedOut] = useState(false);

  // if clicking "Save Image" button, wait for segments before taking the snapshot
  const [isSnapshotStarted, setIsSnapshotStarted] = useState(false);
  useEffect(() => {
    let timeoutId = 0;
    if (isTakingSnapshot) {
      timeoutId = window.setTimeout(() => setTimedOut(true), 20 * 1000);
    }
    return () => {
      window.clearTimeout(timeoutId);
      setTimedOut(false);
    };
  }, [isTakingSnapshot]);

  // wait for segments to load, then download the screen clipping
  const isSnapshotReady =
    (shouldAutoDownload || isSnapshotStarted) && (haveAllSegments || timedOut);

  useEffectOnce({
    callback: () => {
      if (!isSnapshotReady) {
        return;
      }
      // wait a bit more for timeline to load
      console.log(`${elementName} ready -> downloading png`);
      window.setTimeout(
        () => {
          startTransition(() => {
            const elementToSnapshot =
              document.querySelector<HTMLElement>(selector);
            if (elementToSnapshot) {
              onBeforeSnapshot?.(elementToSnapshot);
              setIsSnapshotStarted(true);
              handleDownloadElementPng();
            }
          });
        },
        (haveAllSegments ? 1 : 5) * 1000
      );
    },
    shouldRun: isSnapshotReady,
    dependencies: [
      isSnapshotReady,
      haveAllSegments,
      elementName,
      setIsTakingSnapshot,
      onAfterSnapshot,
      onBeforeSnapshot,
      selector,
      handleDownloadElementPng,
    ],
  });
  return !isAdminUser ? null : isMenuItem ? (
    <StyledCardControlsMenuItem
      className={`btn-download-${elementName}`}
      onClick={e => {
        onMenuItemClick?.();
        e.stopPropagation();
        setIsTakingSnapshot(true);
        // ensure Timeline component is not collapsed, as its contents actively determine `allTimelinesLoaded` state
        document
          .querySelector<HTMLElement>(
            "[data-testId=card-topbar-Timeline].collapsed .btnCollapse"
          )
          ?.click();
        // allow time for the page to render before taking the snapshot
        setTimeout(() => setIsSnapshotStarted(true));
      }}
    >
      <div>
        <IconButton>
          <Image />
        </IconButton>
      </div>
      <div style={{ marginRight: 8 }}>Download Image</div>
    </StyledCardControlsMenuItem>
  ) : (
    <Styles {...{ $customCss: customCss }}>
      <Tooltip title="Save image">
        <IconButton
          size="small"
          className={`btn-download-${elementName}`}
          onClick={() => {
            setIsTakingSnapshot(true);
            // allow time for the page to render before taking the snapshot
            setTimeout(() => setIsSnapshotStarted(true));
          }}
        >
          {(isSnapshotStarted || isTakingSnapshot) && !haveAllSegments ? (
            <Spinner width={12} />
          ) : (
            <Download />
          )}
        </IconButton>
      </Tooltip>
    </Styles>
  );
}

/** Download button for the CheapCanvasProductionSegments; also handles autoDownload via url */
function SnapshotCanvasTimelineElementButton({
  elementName,
  selector,
  customCss,
  onBeforeSnapshot,
  onAfterSnapshot,
  isMenuItem,
  onMenuItemClick,
}: {
  elementName: string;
  /** CSS selector for element you want to snapshot */
  selector: string;
  customCss?: string;
  onBeforeSnapshot?: (elementToSnapshot: HTMLElement) => void;
  onAfterSnapshot?: (elementToSnapshot: HTMLElement) => void;
  isMenuItem?: boolean;
  onMenuItemClick?: () => void;
}) {
  const isAdminUser = useIsAdmin();
  const [isTakingSnapshot, setIsTakingSnapshot] = useIsTakingSnapshot();

  const shouldAutoDownload = window.location.search.includes(
    `autoDownload=${elementName}`
  );
  const visibleProcessIds = useVisibleProcessIds();

  const segments = useSelector(state => state.legacy.segments);
  // check that we have segments for all visible processes
  const segmentsForProcess = visibleProcessIds.map(id => segments[id]);

  // ensure segments span the entire visibleRange (no gaps in the timeline)
  const [allTimelinesLoaded] = useAtom(allTimelinesLoadedAtom);
  const timelinesLoadedValues = Object.values(allTimelinesLoaded);

  const haveAllSegments =
    segmentsForProcess.length > 0 &&
    segmentsForProcess.every(segments => segments && segments.length > 0) &&
    timelinesLoadedValues.length === visibleProcessIds.length &&
    timelinesLoadedValues.every(isLoaded => isLoaded);

  // sometimes (e.g. sbdwarrington) there are gaps in the segments so haveAllSegments will never become true
  // -> instead, set a maximum timeout to wait before taking the snapshot regardless
  const [timedOut, setTimedOut] = useState(false);

  // if clicking "Save Image" button, wait for segments before taking the snapshot
  const [isSnapshotStarted, setIsSnapshotStarted] = useState(false);
  useEffect(() => {
    let timeoutId = 0;
    if (isTakingSnapshot) {
      timeoutId = window.setTimeout(() => setTimedOut(true), 20 * 1000);
    }
    return () => {
      window.clearTimeout(timeoutId);
      setTimedOut(false);
    };
  }, [isTakingSnapshot]);

  const [autoDownloadFinished, setAutoDownloadFinished] = useState(false);

  // wait for segments to load, then download the screen clipping
  const isSnapshotReady =
    ((shouldAutoDownload && !autoDownloadFinished) || isSnapshotStarted) &&
    (haveAllSegments || timedOut);
  const captureAndDownloadScreenshot = useCaptureElementWithCanvas({
    cssSelector: selector,
    elementName,
  });

  useEffect(() => {
    if (!isSnapshotReady) {
      return;
    }
    // wait a bit more for timeline to load
    console.log(`${elementName} ready -> downloading png`);
    window.setTimeout(
      () => {
        startTransition(() => {
          const elementToSnapshot =
            document.querySelector<HTMLElement>(selector);
          if (elementToSnapshot) {
            onBeforeSnapshot?.(elementToSnapshot);
            setIsSnapshotStarted(true);
            (async () => {
              await captureAndDownloadScreenshot();
              setAutoDownloadFinished(true);
              setIsSnapshotStarted(false);
              setIsTakingSnapshot(false);
              onAfterSnapshot?.(elementToSnapshot);
            })();
          }
        });
      },
      (haveAllSegments ? 1 : 5) * 1000
    );
  }, [
    isSnapshotReady,
    captureAndDownloadScreenshot,
    haveAllSegments,
    elementName,
    setIsTakingSnapshot,
    onAfterSnapshot,
    onBeforeSnapshot,
    selector,
  ]);
  return !isAdminUser ? null : isMenuItem ? (
    <StyledCardControlsMenuItem
      className={`btn-download-${elementName}`}
      onClick={e => {
        onMenuItemClick?.();
        e.stopPropagation();
        setIsTakingSnapshot(true);
        // ensure Timeline component is not collapsed, as its contents actively determine `allTimelinesLoaded` state
        document
          .querySelector<HTMLElement>(
            "[data-testId=card-topbar-Timeline].collapsed .btnCollapse"
          )
          ?.click();
        // allow time for the page to render before taking the snapshot
        setTimeout(() => setIsSnapshotStarted(true));
      }}
    >
      <div>
        <IconButton>
          <Image />
        </IconButton>
      </div>
      <div style={{ marginRight: 8 }}>Download Image</div>
    </StyledCardControlsMenuItem>
  ) : (
    <Styles {...{ $customCss: customCss }}>
      <Tooltip title="Save image">
        <IconButton
          size="small"
          className={`btn-download-${elementName}`}
          onClick={() => {
            setIsTakingSnapshot(true);
            // allow time for the page to render before taking the snapshot
            setTimeout(() => setIsSnapshotStarted(true));
          }}
        >
          {(isSnapshotStarted || isTakingSnapshot) && !haveAllSegments ? (
            <Spinner width={12} />
          ) : (
            <Download />
          )}
        </IconButton>
      </Tooltip>
    </Styles>
  );
}

const isTakingSnapshotAtom = atom(false);

/** when taking snapshots of charts e.g. from /snapshots page or clicking "download PNG" button, use this to toggle CSS/other changes as-needed e.g. to make snapshotted elements fit on a single page, or hide/unhide elements */
export function useIsTakingSnapshot(): [
  boolean,
  (isTakingSnapshot: boolean) => void,
] {
  return useAtom(isTakingSnapshotAtom);
}
/** useMount to check if pathname.includes("fromSnapshots") */
export const useDetectIsTakingSnapshot = () => {
  const [, setIsTakingSnapshot] = useIsTakingSnapshot();
  useMount(() => {
    if (window.location.pathname.includes("fromSnapshots")) {
      setIsTakingSnapshot(true);
    }
  });
};
const Styles = styled.div<{
  $customCss?: string;
}>`
  position: absolute;
  top: 2px;
  right: 22px;
  z-index: 99;
  transform: scale(0.8);
  transform-origin: top right;
  ${({ $customCss }) => $customCss}
`;

export const AUTO_DOWNLOAD_IMG = {
  timeline: "timeline",
  pnl: "pnl",
};

export const StyledCardControlsMenuItem = styled(MenuItem)`
  height: 40px;
  padding: 0px 12px 0 6px;
  display: flex;
  justify-content: start;
  align-items: center;
  gap: 8px;
  .MuiSvgIcon-root {
    color: ${darkBlue} !important;
    fill: ${darkBlue} !important;
  }
`;

/** capture a screenshot of an element containing one or more canvases (which need to be redrawn before capture) */

const useCaptureElementWithCanvas = ({
  cssSelector,
  elementName,
}: {
  cssSelector: string;
  elementName: string;
}) => {
  const focusRange = useFocusRange();
  const visibleProcessIds = useVisibleProcessIds();
  const { showTimelineShiftIndicators } = useTimelinesStore();
  const [, setTimelineTooltipStore] = useTimelineTooltipStore();

  const captureAndDownloadScreenshot = useCallback(() => {
    // close the tooltip
    setTimelineTooltipStore(prev => ({
      ...prev,
      selectedProdSegment: null,
      selectedTimelineIdx: null,
      machemetryId: null,
      modal: null,
    }));

    const element = document.querySelector<HTMLElement>(cssSelector);
    if (!element) {
      return;
    }

    const canvases = element.querySelectorAll<HTMLCanvasElement>("canvas");
    const majorTickLines = element.querySelectorAll<SVGLineElement>(
      ".majorTicks.visible line"
    );
    const minorTickLines = element.querySelectorAll<SVGLineElement>(
      ".minorTicks.visible line"
    );
    const outOfFocusPaths =
      element.querySelectorAll<SVGLineElement>(".outOfFocusPath");
    const shiftIndicators = element.querySelectorAll<SVGLineElement>(
      ".vertical-line-white-padding"
    );
    if (!canvases.length) {
      return;
    }

    const combinedCanvas = document.createElement("canvas");
    const ctx = combinedCanvas.getContext("2d");
    if (!ctx) {
      return;
    }

    combinedCanvas.width = element.offsetWidth;
    combinedCanvas.height = element.offsetHeight;

    const drawContent = async () => {
      // first, draw everything except the canvases
      const renderedCanvas = await html2canvas(element, {
        ignoreElements: el =>
          el.tagName.toLowerCase() === "canvas" ||
          Array.from(el.classList).includes("modalVertical"),
        allowTaint: true,
        backgroundColor: null,
        scale: 1,
      });

      ctx.drawImage(renderedCanvas, 0, 0);

      const { left: elementLeft, top: elementTop } =
        element.getBoundingClientRect();

      // then draw the canvases on top
      canvases.forEach(canvas => {
        const { left: canvasLeft, top: canvasTop } =
          canvas.getBoundingClientRect();
        ctx.drawImage(canvas, canvasLeft - elementLeft, canvasTop - elementTop);
      });

      // finally draw the tick lines and out-of-focus paths on top of the canvases
      majorTickLines.forEach(line => {
        const {
          left: x1,
          top: y1,
          right: x2,
          bottom: y2,
        } = line.getBoundingClientRect();
        ctx.strokeStyle = "black";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(x1 - elementLeft, y1 - elementTop);
        ctx.lineTo(x2 - elementLeft, y2 - elementTop);
        ctx.stroke();
        ctx.closePath();
      });
      minorTickLines.forEach(line => {
        const {
          left: x1,
          top: y1,
          right: x2,
          bottom: y2,
        } = line.getBoundingClientRect();
        ctx.strokeStyle = "black";
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        ctx.moveTo(x1 - elementLeft, y1 - elementTop);
        ctx.lineTo(x2 - elementLeft, y2 - elementTop);
        ctx.stroke();
        ctx.closePath();
      });
      outOfFocusPaths.forEach(path => {
        const {
          left: x1,
          top: y1,
          right: x2,
          bottom: y2,
        } = path.getBoundingClientRect();
        ctx.fillStyle = "rgba(88,89,91,0.35)";
        ctx.fillRect(x1 - elementLeft, y1 - elementTop, x2 - x1, y2 - y1);
        ctx.closePath();
      });
      if (showTimelineShiftIndicators) {
        shiftIndicators.forEach(line => {
          const rect = line.getBoundingClientRect();
          ctx.strokeStyle = "#FFFFFF";
          ctx.lineWidth = 6;
          ctx.beginPath();
          ctx.moveTo(rect.left - elementLeft, 0 - elementTop);
          ctx.lineTo(rect.left - elementLeft, combinedCanvas.height);
          ctx.stroke();
          ctx.closePath();
          ctx.strokeStyle = "#000000";
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(rect.left - elementLeft, 0 - elementTop);
          ctx.lineTo(rect.left - elementLeft, combinedCanvas.height);
          ctx.stroke();
          ctx.closePath();
        });
      }
    };

    drawContent().then(() => {
      const screenshot = combinedCanvas.toDataURL("image/png");
      const downloadLink = document.createElement("a");
      downloadLink.href = screenshot;
      const focusRangeReadable = `${format(
        focusRange?.ge ?? 0,
        "yyyy-MM-dd"
      )}_${format(focusRange?.lt ?? 0, "yyyy-MM-dd")}`;
      downloadLink.download = `${elementName}_${visibleProcessIds.join(
        ","
      )}_${focusRangeReadable}.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    });
  }, [
    cssSelector,
    elementName,
    focusRange,
    visibleProcessIds,
    showTimelineShiftIndicators,
    setTimelineTooltipStore,
  ]);

  return captureAndDownloadScreenshot;
};
